import { StatusModal } from '@/components/StatusModal';
import type {
  ProductSuggestion,
  ProductionTerm,
  StockCurrent,
  StockMovement,
} from '@/components/vyroba/types';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { fmtDateTime } from '@/lib/format';
import { type ReactNode, useCallback, useEffect, useState } from 'react';

/** 'YYYY-MM-DD' → 'DD.MM.YYYY'. */
function fmtCzDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}.${m}.${y}` : iso;
}

/** 'YYYY-MM-DD' → 'D.M.' (krátce, bez roku a bez nul) — pro přehled naskladnění. */
function fmtShortCzDate(iso: string): string {
  const [, m, d] = iso.split('-');
  return m && d ? `${Number.parseInt(d, 10)}.${Number.parseInt(m, 10)}.` : iso;
}

type SizeRow = { key: string; size: string | null; order: number | null };

/** Stav zápisu do Shoptetu (zadané do výroby jdou do dostupnosti), z backendu. */
type ShoptetStatus = { ok: boolean; error?: string };

/** Přepnutí dostupnosti varianty v e-shopu podle termínu výroby. */
type AvailabilityStatus = { ok: boolean; availability: string; error?: string };

/** Vlastní stylovaný select — bez ošklivého nativního vzhledu, s vlastní šipkou. */
function SelectBox({
  value,
  onChange,
  children,
  className,
  id,
}: {
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <div className={`relative ${className ?? ''}`}>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full cursor-pointer appearance-none rounded-lg border border-border bg-surface py-2 pl-3 pr-9 text-sm font-medium text-text transition-colors hover:border-text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
      >
        {children}
      </select>
      <svg
        aria-hidden="true"
        viewBox="0 0 20 20"
        fill="none"
        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted"
      >
        <path
          d="M6 8l4 4 4-4"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

/**
 * Zadání do výroby (1. krok výroby) — kusy jdou DO výroby na termín. Nad naší
 * vlastní D1 evidencí (viz migrace 0050). Vyhledá se produkt, dole se vykreslí
 * řádky per velikost (Množství + Termín výroby + Naskladnit). Vpravo obrázek
 * produktu a log „Pohyby ve výrobním systému". Druhý krok bude „Vyskladnění
 * z výroby" (z výroby na sklad).
 */
export function ZadaniDoVyrobyPage() {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<ProductSuggestion[]>([]);
  const [picked, setPicked] = useState<ProductSuggestion | null>(null);
  const [terms, setTerms] = useState<ProductionTerm[]>([]);
  const [bulkTerm, setBulkTerm] = useState('');
  const [qtyByKey, setQtyByKey] = useState<Record<string, string>>({});
  const [termByKey, setTermByKey] = useState<Record<string, string>>({});
  const [current, setCurrent] = useState<StockCurrent[]>([]);
  // Kolik má produkt skladem v e-shopu (feed po 15 minutách).
  const [eshop, setEshop] = useState<{ sizes: { variant_name: string; qty: number }[] }>({
    sizes: [],
  });
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [submitting, setSubmitting] = useState<string | null>(null);
  // Klíče řádků, které se právě zadaly do výroby — krátce probliknou zeleně.
  const [doneKeys, setDoneKeys] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);

  const today = new Date().toISOString().slice(0, 10);

  // Aktivní (dnešní + budoucí) termíny, vzestupně — do selectů i do seznamu.
  const activeTerms = [...terms]
    .filter((t) => t.term_date >= today)
    .sort((a, b) => a.term_date.localeCompare(b.term_date));

  // Řádky velikostí. Bez variant → jeden řádek „celý produkt" (klíč '').
  const sizeRows: SizeRow[] = picked
    ? picked.sizes.length > 0
      ? picked.sizes.map((s, i) => ({ key: s, size: s, order: i + 1 }))
      : [{ key: '', size: null, order: null }]
    : [];

  // Termíny do selectů. Nejbližší budoucí jako výchozí hromadný termín.
  useEffect(() => {
    apiFetch<{ terms: ProductionTerm[] }>('/api/vyroba/terms')
      .then((d) => {
        setTerms(d.terms);
        // Hromadný termín necháváme prázdný — uživatel ho vybere sám.
      })
      .catch(() => setTerms([]));
  }, []);

  const refreshMovements = useCallback(async () => {
    try {
      const d = await apiFetch<{ movements: StockMovement[] }>(
        '/api/vyroba/stock/movements?limit=100',
      );
      setMovements(d.movements);
    } catch {
      /* log není kritický */
    }
  }, []);

  useEffect(() => {
    refreshMovements();
  }, [refreshMovements]);

  // Našeptávač produktů s debounce (stejně jako v NewOrderModal).
  useEffect(() => {
    if (picked || query.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const d = await apiFetch<{ products: ProductSuggestion[] }>(
          `/api/vyroba/products?q=${encodeURIComponent(query.trim())}`,
        );
        if (!cancelled) setSuggestions(d.products);
      } catch {
        if (!cancelled) setSuggestions([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, picked]);

  async function refreshCurrent(code: string) {
    try {
      const d = await apiFetch<{ current: StockCurrent[] }>(
        `/api/vyroba/stock/current?product_code=${encodeURIComponent(code)}`,
      );
      setCurrent(d.current);
    } catch {
      setCurrent([]);
      setEshop({ sizes: [] });
    }
  }

  /** Skladovost v e-shopu per velikost — vedle tlačítka „Naskladnit". */
  async function refreshEshop(code: string) {
    try {
      const d = await apiFetch<{ sizes: { variant_name: string; qty: number }[] }>(
        `/api/vyroba/eshop-stav?product_code=${encodeURIComponent(code)}`,
      );
      setEshop({ sizes: d.sizes });
    } catch {
      setEshop({ sizes: [] });
    }
  }

  function pick(s: ProductSuggestion) {
    setPicked(s);
    setSuggestions([]);
    const keys = s.sizes.length > 0 ? s.sizes : [''];
    const qn: Record<string, string> = {};
    const tn: Record<string, string> = {};
    for (const k of keys) {
      qn[k] = '';
      tn[k] = ''; // řádkové selecty prázdné — termín se bere z hromadného výběru
    }
    setQtyByKey(qn);
    setTermByKey(tn);
    refreshCurrent(s.product_code);
    refreshEshop(s.product_code);
  }

  function reset() {
    setPicked(null);
    setQuery('');
    setQtyByKey({});
    setTermByKey({});
    setCurrent([]);
  }

  // Hromadný termín je výchozí („efektivní") termín pro všechny řádky, které
  // nemají zvolený vlastní. Řádkové selecty proto zůstávají prázdné.
  function changeBulkTerm(v: string) {
    setBulkTerm(v);
  }

  async function naskladnit(
    row: SizeRow,
  ): Promise<{ shoptet?: ShoptetStatus; availability?: AvailabilityStatus } | null> {
    if (!picked) return null;
    const qty = Number.parseInt(qtyByKey[row.key] ?? '', 10);
    // Efektivní termín: vlastní u řádku, jinak hromadný.
    const term = (termByKey[row.key] ?? '') || bulkTerm;
    if (!Number.isInteger(qty) || qty <= 0) {
      setToast({ msg: 'Zadej kladné množství', kind: 'error' });
      return null;
    }
    if (!term) {
      setToast({ msg: 'Vyber termín výroby (hromadně nebo u řádku)', kind: 'error' });
      return null;
    }
    const r = await apiFetch<{ shoptet?: ShoptetStatus; availability?: AvailabilityStatus }>(
      '/api/vyroba/stock',
      {
        method: 'POST',
        body: {
          product_code: picked.product_code,
          variant_name: row.size,
          size_order: row.order,
          product_name: picked.name,
          term_date: term,
          qty,
        },
      },
    );
    return { shoptet: r.shoptet, availability: r.availability };
  }

  // Zvýrazní řádek zeleně (potvrzení, že se zadal do výroby).
  function markDone(key: string) {
    setDoneKeys((p) => ({ ...p, [key]: true }));
  }

  // Zelené řádky svítí, dokud akce běží; po dokončení (submitting=null) zhasnou
  // najednou po 1,5 s. Díky tomu u „Naskladnit vše" zůstanou zelené do konce.
  useEffect(() => {
    if (submitting !== null || Object.keys(doneKeys).length === 0) return;
    const t = setTimeout(() => setDoneKeys({}), 1500);
    return () => clearTimeout(t);
  }, [submitting, doneKeys]);

  async function handleRow(row: SizeRow) {
    setSubmitting(row.key);
    try {
      const res = await naskladnit(row);
      if (res) {
        setQtyByKey((p) => ({ ...p, [row.key]: '' }));
        markDone(row.key);
        if (res.shoptet && !res.shoptet.ok) {
          setToast({
            msg: `Zadáno, ale zápis do Shoptetu selhal: ${res.shoptet.error ?? 'chyba'}`,
            kind: 'error',
          });
        } else if (res.availability && !res.availability.ok) {
          setToast({
            msg: `Zadáno, ale dostupnost v e-shopu se nepřepnula: ${res.availability.error ?? 'chyba'}`,
            kind: 'error',
          });
        } else {
          setToast({
            msg: res.availability?.availability
              ? `Zadáno do výroby · dostupnost: ${res.availability.availability}`
              : 'Zadáno do výroby',
            kind: 'success',
          });
        }
        await Promise.all([refreshCurrent(picked?.product_code ?? ''), refreshMovements()]);
      }
    } catch (e: unknown) {
      setToast({
        msg: e instanceof ApiError ? e.describe() : 'Naskladnění selhalo',
        kind: 'error',
      });
    } finally {
      setSubmitting(null);
    }
  }

  async function handleAll() {
    const filled = sizeRows.filter((r) => {
      const n = Number.parseInt(qtyByKey[r.key] ?? '', 10);
      return Number.isInteger(n) && n > 0;
    });
    if (filled.length === 0) {
      setToast({ msg: 'Nejdřív vyplň množství aspoň u jedné velikosti', kind: 'error' });
      return;
    }
    if (filled.some((r) => !((termByKey[r.key] ?? '') || bulkTerm))) {
      setToast({ msg: 'Vyber termín výroby (hromadně nebo u řádku)', kind: 'error' });
      return;
    }
    setSubmitting('all');
    let ok = 0;
    let failed = 0;
    let shoptetFails = 0;
    let availabilityFails = 0;
    for (const row of filled) {
      try {
        const res = await naskladnit(row);
        if (res) {
          ok++;
          setQtyByKey((p) => ({ ...p, [row.key]: '' }));
          markDone(row.key);
          if (res.shoptet && !res.shoptet.ok) shoptetFails++;
          if (res.availability && !res.availability.ok) availabilityFails++;
        }
      } catch {
        failed++;
      }
    }
    setToast({
      msg:
        availabilityFails > 0 && failed === 0
          ? `Zadáno ${ok}, ale u ${availabilityFails}× se nepřepnula dostupnost`
          : failed > 0
            ? `Zadáno ${ok}, selhalo ${failed}`
            : shoptetFails > 0
              ? `Zadáno ${ok}, ale u ${shoptetFails}× selhal zápis do Shoptetu`
              : `Zadáno ${ok} velikostí`,
      kind: failed > 0 || shoptetFails > 0 || availabilityFails > 0 ? 'error' : 'success',
    });
    await Promise.all([refreshCurrent(picked?.product_code ?? ''), refreshMovements()]);
    setSubmitting(null);
  }

  const busy = submitting !== null;

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text">Zadání do výroby</h1>
        <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
          Vyhledej produkt a zadej jeho velikosti do zvoleného termínu výroby. Zápis jde do naší
          evidence (D1) — „Pohyby ve výrobním systému" vpravo ukazují poslední změny.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* ─── Levý sloupec: hledání + naskladnění ─────────────────────────── */}
        <div className="bg-surface rounded-xl shadow-card p-5 space-y-5">
          {/* Vyhledání produktu */}
          <div>
            <span className="block text-sm font-medium text-text mb-1.5">Produkt</span>
            {picked ? (
              <div className="flex items-center gap-3 border border-border rounded-lg px-3 py-2">
                {picked.image_url && (
                  <img
                    src={picked.image_url}
                    alt=""
                    className="w-10 h-10 object-contain rounded shrink-0"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-text truncate">{picked.name}</div>
                  <div className="text-xs text-text-muted font-mono">{picked.product_code}</div>
                </div>
                <button
                  type="button"
                  onClick={reset}
                  className="text-sm font-bold text-primary hover:underline cursor-pointer shrink-0"
                >
                  Změnit
                </button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Hledej podle kódu nebo názvu…"
                  autoComplete="off"
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                {suggestions.length > 0 && (
                  <ul className="mt-1 border border-border rounded-lg max-h-56 overflow-y-auto divide-y divide-border-light">
                    {suggestions.map((s) => (
                      <li key={s.product_code}>
                        <button
                          type="button"
                          onClick={() => pick(s)}
                          className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-surface-hover transition-colors cursor-pointer"
                        >
                          {s.image_url && (
                            <img
                              src={s.image_url}
                              alt=""
                              className="w-8 h-8 object-contain rounded shrink-0"
                            />
                          )}
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm text-text truncate">{s.name}</span>
                            <span className="block text-xs text-text-muted font-mono">
                              {s.product_code}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>

          {/* Hromadný termín + Naskladnit vše */}
          <div>
            <label htmlFor="bulk-term" className="block text-sm font-medium text-text mb-1.5">
              Termín výroby hromadně
            </label>
            <div className="flex gap-2">
              <SelectBox id="bulk-term" value={bulkTerm} onChange={changeBulkTerm} className="w-32">
                <option value="" />
                {activeTerms.map((t) => (
                  <option key={t.id} value={t.term_date}>
                    {t.is_service ? '⚙️ ' : ''}
                    {fmtCzDate(t.term_date)}
                  </option>
                ))}
              </SelectBox>
              <button
                type="button"
                onClick={handleAll}
                disabled={!picked || busy}
                className="bg-action hover:bg-action-dark disabled:opacity-50 text-white text-sm font-bold rounded-lg px-4 py-2 transition-colors cursor-pointer shrink-0"
              >
                {submitting === 'all' ? 'Naskladňuji…' : 'Naskladnit vše'}
              </button>
            </div>
          </div>

          {/* Aktivní termíny — přehled */}
          {activeTerms.length > 0 && (
            <div>
              <span className="block text-xs font-medium text-text-muted mb-1">
                Aktivní výrobní termíny
              </span>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                {activeTerms.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => changeBulkTerm(t.term_date)}
                    className={`cursor-pointer transition-colors hover:text-primary hover:underline ${
                      bulkTerm === t.term_date
                        ? 'font-semibold text-primary'
                        : 'text-text-secondary'
                    }`}
                  >
                    {t.is_service ? '⚙️ ' : ''}
                    {fmtCzDate(t.term_date)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Tabulka velikostí */}
          {picked ? (
            activeTerms.length === 0 ? (
              <p className="text-sm text-warning bg-warning-light border border-warning/30 rounded-lg px-4 py-3">
                Žádné aktivní termíny výroby. Nejdřív je založ v sekci „Termíny výroby".
              </p>
            ) : (
              <div className="border border-border rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-bg text-left text-xs font-semibold text-text-secondary">
                      <th className="px-3 py-2 w-14">Velikost</th>
                      <th className="px-3 py-2 w-24">Množství</th>
                      <th className="px-3 py-2">Termín výroby</th>
                      <th className="px-3 py-2" />
                      <th className="px-3 py-2 whitespace-nowrap">V e-shopu</th>
                      <th className="w-full p-0" />
                    </tr>
                  </thead>
                  <tbody>
                    {sizeRows.map((row) => {
                      const rowTerm = termByKey[row.key] ?? '';
                      // Už naskladněné množství této velikosti – per termín, seřazené.
                      // Skladovost v e-shopu pro tuhle velikost (feed po 15 min).
                      const eshopQty =
                        eshop.sizes.find((e) => e.variant_name === (row.size ?? ''))?.qty ?? null;
                      const rowStock = current
                        .filter((c) => (c.variant_name ?? '') === (row.size ?? '') && c.qty !== 0)
                        .sort((a, b) => a.term_date.localeCompare(b.term_date));
                      return (
                        <tr
                          key={row.key}
                          className={`border-t border-border-light transition-colors duration-700 ${
                            doneKeys[row.key] ? 'bg-success-light' : ''
                          }`}
                        >
                          <td className="px-3 py-2 font-medium text-text">
                            {row.size ?? 'Celý produkt'}
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              min="1"
                              value={qtyByKey[row.key] ?? ''}
                              onChange={(e) =>
                                setQtyByKey((p) => ({ ...p, [row.key]: e.target.value }))
                              }
                              className="w-20 border border-border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <SelectBox
                              value={rowTerm}
                              onChange={(v) => setTermByKey((p) => ({ ...p, [row.key]: v }))}
                              className="w-32"
                            >
                              <option value="" />
                              {activeTerms.map((t) => (
                                <option key={t.id} value={t.term_date}>
                                  {t.is_service ? '⚙️ ' : ''}
                                  {fmtCzDate(t.term_date)}
                                </option>
                              ))}
                            </SelectBox>
                          </td>
                          <td className="px-3 py-2">
                            <button
                              type="button"
                              onClick={() => handleRow(row)}
                              disabled={busy}
                              className="bg-action hover:bg-action-dark disabled:opacity-50 text-white text-sm font-bold rounded-md px-4 py-1.5 transition-colors cursor-pointer"
                            >
                              {submitting === row.key ? 'Zapisuji…' : 'Naskladnit'}
                            </button>
                          </td>
                          <td className="px-3 py-2 text-right text-sm whitespace-nowrap">
                            {eshopQty === null ? (
                              <span className="text-text-muted">—</span>
                            ) : (
                              <span
                                className={
                                  eshopQty > 0
                                    ? 'font-semibold text-text'
                                    : 'font-semibold text-danger'
                                }
                              >
                                {eshopQty} ks
                              </span>
                            )}
                          </td>
                          <td className="w-full px-3 py-2 text-center align-middle text-sm text-text-secondary whitespace-nowrap">
                            {rowStock.map((c) => (
                              <div key={c.term_date}>
                                {fmtShortCzDate(c.term_date)} – {c.qty} ks
                              </div>
                            ))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            <p className="text-sm text-text-muted">Vyhledej produkt, dole se vykreslí velikosti.</p>
          )}
        </div>

        {/* ─── Pravý sloupec: obrázek + pohyby ─────────────────────────────── */}
        <div className="space-y-6">
          {picked?.image_url && (
            <div className="bg-surface rounded-xl shadow-card p-5 flex justify-center">
              <img
                src={picked.image_url}
                alt={picked.name}
                className="max-h-64 object-contain rounded-lg"
              />
            </div>
          )}

          <div className="bg-surface rounded-xl shadow-card overflow-hidden">
            <div className="px-5 py-3 border-b border-border">
              <h2 className="text-sm font-bold text-text">Pohyby ve výrobním systému</h2>
            </div>
            <div className="max-h-[32rem] overflow-y-auto overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-bg">
                  <tr className="text-left font-semibold text-text-secondary">
                    <th className="px-3 py-2">Datum</th>
                    <th className="px-3 py-2">Produkt</th>
                    <th className="px-3 py-2">Termín</th>
                    <th className="px-3 py-2 text-right">Změna</th>
                    <th className="px-3 py-2 text-right">Stav</th>
                    <th className="px-3 py-2">Uživatel</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-3 py-6 text-center text-text-muted">
                        Zatím žádné pohyby.
                      </td>
                    </tr>
                  ) : (
                    movements.map((m) => (
                      <tr key={m.id} className="border-t border-border-light">
                        <td className="px-3 py-2 whitespace-nowrap text-text-secondary">
                          {fmtDateTime(m.created_at)}
                        </td>
                        <td className="px-3 py-2 font-mono text-text whitespace-nowrap">
                          {/* Skutečné SKU z katalogu — popisek velikosti („50-52 cm")
                              se nedá lepit za kód, vzniklo by „C1009/50-52 cm". */}
                          {m.sku ??
                            (m.variant_name
                              ? `${m.product_code}/${m.variant_name}`
                              : m.product_code)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-text-secondary">
                          {fmtCzDate(m.term_date)}
                        </td>
                        <td
                          className={`px-3 py-2 text-right font-semibold ${
                            m.qty_change < 0 ? 'text-danger' : 'text-success'
                          }`}
                        >
                          {m.qty_change > 0 ? `+${m.qty_change}` : m.qty_change}
                        </td>
                        <td className="px-3 py-2 text-right text-text-secondary whitespace-nowrap">
                          {m.qty_before} → {m.qty_after}
                        </td>
                        <td className="px-3 py-2 text-text-secondary truncate max-w-[10rem]">
                          {m.created_by}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {toast && (
        <StatusModal message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />
      )}
    </>
  );
}
